import React, { Component, useState, ReactNode, CSSProperties } from 'react';
import { Auth } from 'aws-amplify';
import { reportError } from './utils/logError';

const loggedErrors = new Set();

/**
 * Trims React's verbose componentStack to the meaningful chain — the failing
 * component and its ancestors up to the error boundary — as names only. Drops
 * the bundle URLs and the router/provider/emotion noise below the boundary.
 */
export function summarizeComponentStack(stack?: string | null): string[] {
  if (!stack) return [];
  const components: string[] = [];
  for (const line of stack.split('\n')) {
    const name = line.trim().split('@')[0].trim();
    if (!name) continue;
    if (/^Error(Catcher|Boundary)$/.test(name)) break; // stop at the catching boundary
    components.push(name);
    if (components.length >= 12) break;
  }
  return components;
}

class ErrorCatcher extends Component<{ onError: (error: Error, errorInfo: React.ErrorInfo) => void; children: ReactNode }> {
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.props.onError(error, errorInfo);
  }

  render() {
    return this.props.children;
  }
}

const styles: { [key: string]: CSSProperties } = {
  errorContainer: {
    padding: '30px',
    textAlign: 'center',
    backgroundColor: '#f7f7f7',
    borderRadius: '10px',
    margin: '50px',
    color: '#424242',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'space-evenly',
    gap: '20px',
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
  },
  errorTitle: {
    fontSize: '30px',
    fontWeight: 600,
    color: '#1B3B6F',
  },
  refreshButton: {
    padding: '10px 20px',
    fontSize: '16px',
    cursor: 'pointer',
    backgroundColor: '#1B3B6F',
    color: 'white',
    border: 'none',
    borderRadius: '5px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
    transition: 'background-color 0.3s',
  },
};

const ErrorBoundary: React.FC<{ children: React.ReactNode; name?: string }> = ({ children, name }) => {
  const [hasError, setHasError] = useState(false);

  const handleError = async (error: Error, errorInfo: React.ErrorInfo) => {
    const errorName = error.toString();

    // Show the fallback immediately, regardless of logging.
    setHasError(true);

    if (loggedErrors.has(errorName)) return;
    loggedErrors.add(errorName);

    // Best-effort user lookup — must never block or skip the actual report.
    let userEmail: string | undefined;
    try {
      userEmail = (await Auth.currentAuthenticatedUser())?.attributes?.email;
    } catch {
      userEmail = undefined;
    }

    reportError('tpn-client/error-handler', `${process.env.REACT_APP_ENVIRONMENT?.toLowerCase() || 'dev'}-errors`, {
      type: 'render-error',
      // Which boundary/area failed — readable even when the JS is minified.
      boundary: name ?? 'app-root',
      errorMessage: errorName,
      user: userEmail,
      location: window.location.href,
      components: summarizeComponentStack(errorInfo.componentStack),
    });
  };

  if (hasError) {
    return (
    <div style={styles.errorContainer}>
        <img src={`${process.env.PUBLIC_URL}/assets/TPN_BlueLogoTransparent_1280.png`} alt="TPN Logo" height="50" />
        <div style={styles.errorTitle}>Oops! Something went wrong.</div>
        <div>We have recorded this issue and our team is working on it. Please refresh your page or try again shortly.</div>
        <button
        onClick={() => window.location.reload()}
        style={styles.refreshButton}
        onMouseOver={({ currentTarget }) => (currentTarget.style.backgroundColor = '#2E5078')}
        onMouseOut={({ currentTarget }) => (currentTarget.style.backgroundColor = '#1B3B6F')}
        >
        Refresh page
        </button>
    </div>
    );
  }

  return (
    <ErrorCatcher onError={handleError}>
    {children}
    </ErrorCatcher>
  );
};

export default ErrorBoundary;
